import { Radio, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { BarrierDirection } from '@/types/barriers';

interface DirectionSelectorProps {
  readonly value: BarrierDirection;
  readonly hintSet: boolean;
  readonly onChange: (value: BarrierDirection) => void;
  readonly onPickHint: () => void;
  readonly pickingHint: boolean;
}

const ALLOWED: readonly BarrierDirection[] = ['both', 'forward', 'backward'];

function isBarrierDirection(value: unknown): value is BarrierDirection {
  return typeof value === 'string' && (ALLOWED as readonly string[]).includes(value);
}

export default function DirectionSelector({
  value,
  hintSet,
  onChange,
  onPickHint,
  pickingHint,
}: DirectionSelectorProps) {
  const { t } = useTranslation();
  const requiresHint = value !== 'both';

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Radio.Group
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          if (isBarrierDirection(next)) {
            onChange(next);
          }
        }}
        optionType="default"
      >
        <Radio value="both">{t('barriers.direction.both')}</Radio>
        <Radio value="forward">{t('barriers.direction.forward')}</Radio>
        <Radio value="backward">{t('barriers.direction.backward')}</Radio>
      </Radio.Group>

      {requiresHint && (
        <div>
          <Typography.Text type={hintSet ? 'success' : 'warning'}>
            {hintSet ? t('barriers.hint.ready') : t('barriers.hint.required')}
          </Typography.Text>
          {' '}
          <Typography.Link
            onClick={onPickHint}
            disabled={pickingHint}
          >
            {pickingHint
              ? t('barriers.hint.clickMap')
              : hintSet
                ? t('barriers.hint.change')
                : t('barriers.hint.pick')}
          </Typography.Link>
        </div>
      )}
    </Space>
  );
}
